/** Seminar/ujian event live queries. */

import { useMemo } from 'react';
import { COL } from '../../collections';
import { where, orderBy } from '../../collections';
import { useLiveQuery } from '../hooks';
import type { SkripsiEventDoc } from '../../types';

export function useEvents(skripsiId: string | null) {
  return useLiveQuery<SkripsiEventDoc>(
    skripsiId ? COL.events : null,
    skripsiId ? [where('skripsiId', '==', skripsiId), orderBy('dateTime', 'asc')] : [],
    Boolean(skripsiId),
  );
}

export function useUpcomingEvents(days = 14) {
  // Compute the query window ONCE per `days` value. Recreating from/to on every
  // render would change the subscription key in useLiveQuery, causing the
  // snapshot to tear down and resubscribe in a loop.
  const constraints = useMemo(() => {
    const from = new Date(Date.now() - 86_400_000).toISOString();
    const to = new Date(Date.now() + days * 86_400_000).toISOString();
    return [where('dateTime', '>=', from), where('dateTime', '<=', to), orderBy('dateTime', 'asc')];
  }, [days]);
  return useLiveQuery<SkripsiEventDoc>(COL.events, constraints);
}

/**
 * Events across a window spanning [now - historyDays, now + futureDays] so a
 * user sees their seminar/ujian history alongside upcoming ones. The query
 * window is memoized per (futureDays, historyDays) to keep the subscription
 * key stable.
 */
export function useEventsTimeline(futureDays = 45, historyDays = 400) {
  const constraints = useMemo(() => {
    const from = new Date(Date.now() - historyDays * 86_400_000).toISOString();
    const to = new Date(Date.now() + futureDays * 86_400_000).toISOString();
    return [where('dateTime', '>=', from), where('dateTime', '<=', to), orderBy('dateTime', 'asc')];
  }, [futureDays, historyDays]);
  return useLiveQuery<SkripsiEventDoc>(COL.events, constraints);
}
